import { CommonModule, DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ChartModule } from 'primeng/chart';
import { DatePickerModule } from 'primeng/datepicker';
import { MultiSelectModule } from 'primeng/multiselect';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import {
  MonthlyConversationReport,
  ReportAccount,
  ReportChannel,
  ReportsQuery,
  ReportsService
} from 'src/app/services/reports.service';
import * as XLSX from 'xlsx-js-style';

interface ReportAccountOption {
  label: string;
  value: string | null;
}

interface ReportChannelOption {
  label: string;
  value: string;
  icon: string;
}

interface ReportKpi {
  label: string;
  value: number;
  icon: string;
  numberFormat?: string;
}

interface ReportRow extends MonthlyConversationReport {
  rowKey: string;
  channelDetails: ReportChannel[];
}

interface ReportTotals {
  conversations: number;
  messages: number;
  inbound: number;
  outbound: number;
  activeChannels: number;
  averageMessagesPerConversation: number;
  inboundPercentage: number;
  outboundPercentage: number;
}

interface ChannelAggregate extends ReportChannel {
  displayName: string;
  percentage: number;
}

interface MonthlyTrend {
  key: string;
  label: string;
  sortTime: number;
  conversations: number;
  messages: number;
  inbound: number;
  outbound: number;
}

@Component({
  selector: 'app-reports',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    ChartModule,
    DatePickerModule,
    DecimalPipe,
    MultiSelectModule,
    ProgressSpinnerModule,
    SelectModule,
    TableModule,
    ToastModule
  ],
  providers: [MessageService],
  templateUrl: './reports.component.html',
  styleUrl: './reports.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReportsComponent implements OnInit {
  accountOptions: ReportAccountOption[] = [
    { label: 'Todas las cuentas', value: null }
  ];
  channelOptions: ReportChannelOption[] = [];
  selectedChannels: string[] = [];

  fromDate: Date | null = null;
  toDate: Date | null = null;
  selectedAccountId: string | null = null;

  reports: ReportRow[] = [];
  kpis: ReportKpi[] = [];
  totals: ReportTotals = this.emptyTotals();
  channelAggregates: ChannelAggregate[] = [];
  monthlyTrends: MonthlyTrend[] = [];
  consistencyWarnings: string[] = [];
  expandedRows: Record<string, boolean> = {};

  inboundOutboundChartData: Record<string, unknown> = { labels: [], datasets: [] };
  channelChartData: Record<string, unknown> = { labels: [], datasets: [] };
  monthlyMessagesChartData: Record<string, unknown> = { labels: [], datasets: [] };
  monthlyConversationsChartData: Record<string, unknown> = { labels: [], datasets: [] };
  donutChartOptions: Record<string, unknown> = {};
  chartOptions: Record<string, unknown> = {};
  barChartOptions: Record<string, unknown> = {};

  loading = false;
  accountsLoading = false;
  exporting = false;
  hasSearched = false;
  errorMessage = '';
  private lastQueriedFilters: ReportsQuery | null = null;

  constructor(
    private readonly reportsService: ReportsService,
    private readonly messageService: MessageService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.initializeChartOptions();
    void this.preloadAccounts();
  }

  get hasResults(): boolean {
    return this.reports.length > 0;
  }

  get canExport(): boolean {
    return this.hasResults
      && !this.loading
      && !this.exporting
      && this.lastQueriedFilters !== null
      && this.areQueriesEqual(this.buildQuery(), this.lastQueriedFilters);
  }

  async consultReports(): Promise<void> {
    if (this.loading || this.accountsLoading) {
      return;
    }

    this.errorMessage = '';
    const query = this.buildQuery();

    if (query.from && query.to && query.from > query.to) {
      this.setError('La fecha desde debe ser menor o igual a la fecha hasta.');
      return;
    }

    this.loading = true;
    this.cdr.markForCheck();

    try {
      const response = await this.reportsService.getReports(query);
      this.updateAccountOptions(response.accounts);
      this.updateChannelOptions(response.reports);
      this.reports = response.reports
        .map((report, index) => this.toReportRow(report, index))
        .sort((left, right) => this.getReportSortTime(left.month) - this.getReportSortTime(right.month));
      this.hasSearched = true;
      this.lastQueriedFilters = { ...query };
      this.expandedRows = {};
      this.recomputeDerivedData();
    } catch (error) {
      this.setError(this.getFriendlyErrorMessage(error));
    } finally {
      this.loading = false;
      this.cdr.markForCheck();
    }
  }

  exportReportsToExcel(): void {
    if (!this.canExport) {
      return;
    }

    this.exporting = true;
    this.cdr.markForCheck();

    try {
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, this.createReportsWorksheet(), 'Reports');
      XLSX.utils.book_append_sheet(workbook, this.createChannelsWorksheet(), 'Canales');
      XLSX.writeFile(workbook, this.buildExportFilename(), {
        bookType: 'xlsx',
        compression: true
      });
    } catch {
      this.setError('No se pudo generar el archivo Excel. Intenta nuevamente.');
    } finally {
      this.exporting = false;
      this.cdr.markForCheck();
    }
  }

  formatMonth(value: string): string {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    const formatted = new Intl.DateTimeFormat('es', {
      month: 'long',
      year: 'numeric'
    }).format(date);

    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  }

  formatChannelName(value: string): string {
    return value
      .replace(/[_-]+/g, ' ')
      .trim()
      .toLowerCase()
      .replace(/(^|\s)\S/g, character => character.toUpperCase()) || 'Sin canal';
  }

  formatPercentage(value: number): string {
    return `${value.toLocaleString('es-MX', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
  }

  private async preloadAccounts(): Promise<void> {
    this.accountsLoading = true;
    this.cdr.markForCheck();

    try {
      const response = await this.reportsService.getReports();
      this.updateAccountOptions(response.accounts);
      this.updateChannelOptions(response.reports);
    } catch (error) {
      this.setError(this.getFriendlyErrorMessage(error));
    } finally {
      this.accountsLoading = false;
      this.cdr.markForCheck();
    }
  }

  private recomputeDerivedData(): void {
    this.totals = this.calculateTotals(this.reports);
    this.channelAggregates = this.aggregateChannels(this.reports, this.totals.messages);
    this.monthlyTrends = this.aggregateMonthlyTrends(this.reports);
    this.consistencyWarnings = this.validateConsistency(this.reports);
    this.kpis = this.buildKpis();
    this.buildChartData();
  }

  private calculateTotals(reports: ReportRow[]): ReportTotals {
    const totals = reports.reduce((result, report) => ({
      conversations: result.conversations + report.conversationCount,
      messages: result.messages + report.messageCount,
      inbound: result.inbound + report.inboundMessageCount,
      outbound: result.outbound + report.outboundMessageCount
    }), { conversations: 0, messages: 0, inbound: 0, outbound: 0 });
    const activeChannels = new Set(reports.flatMap(report => report.channels.map(channel => channel.channel))).size;

    return {
      ...totals,
      activeChannels,
      averageMessagesPerConversation: totals.conversations > 0 ? totals.messages / totals.conversations : 0,
      inboundPercentage: totals.messages > 0 ? (totals.inbound / totals.messages) * 100 : 0,
      outboundPercentage: totals.messages > 0 ? (totals.outbound / totals.messages) * 100 : 0
    };
  }

  private aggregateChannels(reports: ReportRow[], totalMessages: number): ChannelAggregate[] {
    const aggregates = new Map<string, ReportChannel>();

    for (const report of reports) {
      for (const channel of report.channels) {
        const current = aggregates.get(channel.channel) ?? {
          channel: channel.channel,
          messageCount: 0,
          inboundMessageCount: 0,
          outboundMessageCount: 0
        };

        current.messageCount += channel.messageCount;
        current.inboundMessageCount += channel.inboundMessageCount;
        current.outboundMessageCount += channel.outboundMessageCount;
        aggregates.set(channel.channel, current);
      }
    }

    return [...aggregates.values()]
      .sort((left, right) => right.messageCount - left.messageCount)
      .map(channel => ({
        ...channel,
        displayName: this.formatChannelName(channel.channel),
        percentage: totalMessages > 0 ? (channel.messageCount / totalMessages) * 100 : 0
      }));
  }

  private aggregateMonthlyTrends(reports: ReportRow[]): MonthlyTrend[] {
    const trends = new Map<string, MonthlyTrend>();

    for (const report of reports) {
      const key = this.formatMonthKey(report.month);
      const current = trends.get(key) ?? {
        key,
        label: this.formatMonth(report.month),
        sortTime: this.getReportSortTime(report.month),
        conversations: 0,
        messages: 0,
        inbound: 0,
        outbound: 0
      };

      current.conversations += report.conversationCount;
      current.messages += report.messageCount;
      current.inbound += report.inboundMessageCount;
      current.outbound += report.outboundMessageCount;
      trends.set(key, current);
    }

    return [...trends.values()].sort((left, right) => left.sortTime - right.sortTime);
  }

  private validateConsistency(reports: ReportRow[]): string[] {
    const warnings: string[] = [];

    for (const report of reports) {
      const directionTotal = report.inboundMessageCount + report.outboundMessageCount;
      const channelTotal = report.channels.reduce((total, channel) => total + channel.messageCount, 0);

      if (report.messageCount !== directionTotal) {
        warnings.push(`${this.formatMonth(report.month)} · ${report.accountName}: mensajes no coincide con recibidos + enviados.`);
      }

      if (report.messageCount !== channelTotal) {
        warnings.push(`${this.formatMonth(report.month)} · ${report.accountName}: mensajes no coincide con el detalle por canal.`);
      }
    }

    return [...new Set(warnings)];
  }

  private buildKpis(): ReportKpi[] {
    return [
      { label: 'Conversaciones iniciadas', value: this.totals.conversations, icon: 'pi pi-comments' },
      { label: 'Total de mensajes', value: this.totals.messages, icon: 'pi pi-envelope' },
      { label: 'Mensajes recibidos', value: this.totals.inbound, icon: 'pi pi-arrow-down-left' },
      { label: 'Mensajes enviados', value: this.totals.outbound, icon: 'pi pi-arrow-up-right' },
      { label: 'Canales activos', value: this.totals.activeChannels, icon: 'pi pi-share-alt' },
      {
        label: 'Mensajes por conversación iniciada (promedio)',
        value: this.totals.averageMessagesPerConversation,
        icon: 'pi pi-chart-line',
        numberFormat: '1.1-1'
      }
    ];
  }

  private buildChartData(): void {
    const primaryChannels = this.channelAggregates.slice(0, 8);
    const labels = this.monthlyTrends.map(trend => trend.label);

    this.inboundOutboundChartData = {
      labels: ['Recibidos', 'Enviados'],
      datasets: [{
        data: [this.totals.inbound, this.totals.outbound],
        backgroundColor: ['#3B82F6', '#10B981'],
        hoverBackgroundColor: ['#2563EB', '#059669'],
        borderWidth: 0
      }]
    };

    this.channelChartData = {
      labels: primaryChannels.map(channel => channel.displayName),
      datasets: [{
        label: 'Mensajes',
        data: primaryChannels.map(channel => channel.messageCount),
        backgroundColor: '#6366F1',
        borderRadius: 5,
        barThickness: 18
      }]
    };

    this.monthlyMessagesChartData = {
      labels,
      datasets: [
        { label: 'Total de mensajes', data: this.monthlyTrends.map(trend => trend.messages), borderColor: '#3B82F6', backgroundColor: 'rgba(59, 130, 246, .12)', tension: .35, fill: true },
        { label: 'Recibidos', data: this.monthlyTrends.map(trend => trend.inbound), borderColor: '#10B981', backgroundColor: 'transparent', tension: .35, fill: false },
        { label: 'Enviados', data: this.monthlyTrends.map(trend => trend.outbound), borderColor: '#F59E0B', backgroundColor: 'transparent', tension: .35, fill: false }
      ]
    };

    this.monthlyConversationsChartData = {
      labels,
      datasets: [{
        label: 'Conversaciones iniciadas',
        data: this.monthlyTrends.map(trend => trend.conversations),
        backgroundColor: '#8B5CF6',
        borderColor: '#7C3AED',
        borderRadius: 5
      }]
    };
  }

  private initializeChartOptions(): void {
    const textColor = '#64748B';
    const gridColor = 'rgba(148, 163, 184, .18)';
    const tooltipTitle = (items: any[]): string => items[0]?.label ?? '';
    const tooltipLabel = (context: any): string => `${context.dataset.label ?? context.label}: ${this.formatNumber(context.parsed?.y ?? context.parsed ?? context.raw)}`;

    this.donutChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: { callbacks: { label: (context: any) => `${context.label}: ${this.formatNumber(context.raw)} (${this.formatPercentage(this.getChartPercentage(context.raw))})` } }
      }
    };

    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { intersect: false, mode: 'index' },
      plugins: {
        legend: { position: 'bottom', labels: { color: textColor, usePointStyle: true, padding: 18 } },
        tooltip: { callbacks: { title: tooltipTitle, label: tooltipLabel } }
      },
      scales: {
        x: { ticks: { color: textColor }, grid: { color: gridColor } },
        y: { beginAtZero: true, ticks: { color: textColor }, grid: { color: gridColor } }
      }
    };

    this.barChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: 'y',
      plugins: {
        legend: { display: false },
        tooltip: { callbacks: { label: tooltipLabel } }
      },
      scales: {
        x: { beginAtZero: true, ticks: { color: textColor }, grid: { color: gridColor } },
        y: { ticks: { color: textColor }, grid: { display: false } }
      }
    };
  }

  private getChartPercentage(value: number): number {
    return this.totals.messages > 0 ? (value / this.totals.messages) * 100 : 0;
  }

  private formatNumber(value: unknown): string {
    return Number(value || 0).toLocaleString('es-MX');
  }

  private toReportRow(report: MonthlyConversationReport, index: number): ReportRow {
    return {
      ...report,
      rowKey: `${report.accountId}|${report.month}|${index}`,
      channelDetails: [...report.channels].sort((left, right) => right.messageCount - left.messageCount)
    };
  }

  private buildQuery(): ReportsQuery {
    const query: ReportsQuery = {};
    const from = this.formatDateForQuery(this.fromDate);
    const to = this.formatDateForQuery(this.toDate);

    if (from) {
      query.from = from;
    }

    if (to) {
      query.to = to;
    }

    if (this.selectedAccountId) {
      query.accountId = this.selectedAccountId;
    }

    if (this.selectedChannels.length > 0) {
      query.channels = [...this.selectedChannels];
    }

    return query;
  }

  private formatDateForQuery(date: Date | null): string | undefined {
    if (!date) {
      return undefined;
    }

    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  private formatMonthKey(value: string): string {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return `${date.getFullYear()}-${date.getMonth() + 1}`;
  }

  private getReportSortTime(value: string): number {
    const time = new Date(value).getTime();
    return Number.isNaN(time) ? Number.MAX_SAFE_INTEGER : time;
  }

  private createReportsWorksheet(): XLSX.WorkSheet {
    const rows: Array<Array<string | number>> = [
      ['Reporte de conversaciones de agentes IA'],
      [],
      ['Fecha desde:', this.formatExportDate(this.fromDate)],
      ['Fecha hasta:', this.formatExportDate(this.toDate)],
      ['Cuenta:', this.getSelectedAccountLabel()],
      ['Canales:', this.getSelectedChannelsLabel()],
      ['Generado:', this.formatGeneratedDate(new Date())],
      [],
      ['RESUMEN']
    ];
    const summaryTitleRow = rows.length - 1;
    const summaryRowsStart = rows.length;

    for (const kpi of this.kpis) {
      rows.push([kpi.label, kpi.value]);
    }

    rows.push([]);
    const tableHeaderRow = rows.length;
    rows.push(['Mes', 'Cuenta', 'Conversaciones iniciadas', 'Total mensajes', 'Recibidos', 'Enviados']);

    for (const report of this.reports) {
      rows.push([
        this.formatMonth(report.month),
        report.accountName,
        report.conversationCount,
        report.messageCount,
        report.inboundMessageCount,
        report.outboundMessageCount
      ]);
    }

    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    worksheet['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 5 } },
      { s: { r: summaryTitleRow, c: 0 }, e: { r: summaryTitleRow, c: 5 } }
    ];
    worksheet['!cols'] = [
      { wch: 24 }, { wch: 32 }, { wch: 25 }, { wch: 18 }, { wch: 14 }, { wch: 14 }
    ];
    worksheet['!autofilter'] = {
      ref: `A${tableHeaderRow + 1}:F${rows.length}`
    };

    this.styleWorksheet(worksheet, tableHeaderRow, rows.length - tableHeaderRow - 1, 6, summaryRowsStart, this.kpis.length);
    return worksheet;
  }

  private createChannelsWorksheet(): XLSX.WorkSheet {
    const rows: Array<Array<string | number>> = [
      ['Detalle de canales · Reporte de conversaciones de agentes IA'],
      [],
      ['Fecha desde:', this.formatExportDate(this.fromDate)],
      ['Fecha hasta:', this.formatExportDate(this.toDate)],
      ['Cuenta:', this.getSelectedAccountLabel()],
      ['Canales:', this.getSelectedChannelsLabel()],
      ['Generado:', this.formatGeneratedDate(new Date())],
      [],
      ['Mes', 'Cuenta', 'Canal', 'Total mensajes', 'Recibidos', 'Enviados']
    ];
    const tableHeaderRow = rows.length - 1;

    for (const report of this.reports) {
      for (const channel of report.channelDetails) {
        rows.push([
          this.formatMonth(report.month),
          report.accountName,
          this.formatChannelName(channel.channel),
          channel.messageCount,
          channel.inboundMessageCount,
          channel.outboundMessageCount
        ]);
      }
    }

    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    worksheet['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 5 } }];
    worksheet['!cols'] = [
      { wch: 24 }, { wch: 32 }, { wch: 26 }, { wch: 18 }, { wch: 14 }, { wch: 14 }
    ];
    worksheet['!autofilter'] = {
      ref: `A${tableHeaderRow + 1}:F${rows.length}`
    };

    this.styleWorksheet(worksheet, tableHeaderRow, rows.length - tableHeaderRow - 1, 6);
    return worksheet;
  }

  private styleWorksheet(
    worksheet: XLSX.WorkSheet,
    tableHeaderRow: number,
    dataRowCount: number,
    columnCount: number,
    summaryRowsStart?: number,
    summaryRowCount = 0
  ): void {
    const thinBorder = { bottom: { style: 'thin', color: { rgb: 'D9E2F3' } } };
    const titleStyle = {
      font: { bold: true, sz: 16, color: { rgb: '17365D' } },
      alignment: { horizontal: 'left', vertical: 'center' }
    };
    const sectionStyle = {
      font: { bold: true, color: { rgb: '17365D' } },
      fill: { patternType: 'solid', fgColor: { rgb: 'EAF2F8' } },
      alignment: { horizontal: 'left', vertical: 'center' },
      border: thinBorder
    };
    const tableHeaderStyle = {
      font: { bold: true, color: { rgb: 'FFFFFF' } },
      fill: { patternType: 'solid', fgColor: { rgb: '1F4E78' } },
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
      border: thinBorder
    };
    const textCellStyle = {
      alignment: { horizontal: 'left', vertical: 'center' },
      border: thinBorder
    };
    const numberCellStyle = {
      alignment: { horizontal: 'right', vertical: 'center' },
      border: thinBorder,
      numFmt: '#,##0'
    };

    this.applyWorksheetStyle(worksheet, 'A1', titleStyle);
    for (let row = 2; row <= 7; row += 1) {
      this.applyWorksheetStyle(worksheet, `A${row}`, { font: { bold: true } });
      this.applyWorksheetStyle(worksheet, `B${row}`, textCellStyle);
    }

    if (summaryRowsStart !== undefined) {
      this.applyWorksheetStyle(worksheet, `A${summaryRowsStart}`, sectionStyle);
      for (let row = summaryRowsStart + 1; row < summaryRowsStart + summaryRowCount + 1; row += 1) {
        this.applyWorksheetStyle(worksheet, `A${row}`, sectionStyle);
        this.applyWorksheetStyle(worksheet, `B${row}`, numberCellStyle);
      }
    }

    for (let column = 0; column < columnCount; column += 1) {
      this.applyWorksheetStyle(worksheet, XLSX.utils.encode_cell({ r: tableHeaderRow, c: column }), tableHeaderStyle);
    }

    for (let row = tableHeaderRow + 1; row <= tableHeaderRow + dataRowCount; row += 1) {
      this.applyWorksheetStyle(worksheet, `A${row + 1}`, textCellStyle);
      this.applyWorksheetStyle(worksheet, `B${row + 1}`, textCellStyle);
      this.applyWorksheetStyle(worksheet, `C${row + 1}`, textCellStyle);
      for (let column = 3; column < columnCount; column += 1) {
        this.applyWorksheetStyle(worksheet, XLSX.utils.encode_cell({ r: row, c: column }), numberCellStyle);
      }
    }
  }

  private applyWorksheetStyle(worksheet: XLSX.WorkSheet, address: string, style: object): void {
    const cell = worksheet[address];
    if (cell) {
      cell.s = style;
    }
  }

  private formatExportDate(date: Date | null): string {
    if (!date) {
      return 'Todas';
    }

    return `${`${date.getDate()}`.padStart(2, '0')}/${`${date.getMonth() + 1}`.padStart(2, '0')}/${date.getFullYear()}`;
  }

  private formatGeneratedDate(date: Date): string {
    return `${this.formatExportDate(date)} ${`${date.getHours()}`.padStart(2, '0')}:${`${date.getMinutes()}`.padStart(2, '0')}`;
  }

  private getSelectedAccountLabel(): string {
    if (!this.selectedAccountId) {
      return 'Todas las cuentas';
    }

    return this.accountOptions.find(option => option.value === this.selectedAccountId)?.label ?? 'Cuenta seleccionada';
  }

  private buildExportFilename(): string {
    const query = this.buildQuery();
    const parts = ['reports'];

    if (this.selectedAccountId) {
      parts.push(this.slugify(this.getSelectedAccountLabel()));
    }

    if (query.from) {
      parts.push(query.from);
    }

    if (query.to) {
      parts.push(query.to);
    }

    if (parts.length === 1) {
      parts.push('todas-las-cuentas');
    }

    return `${parts.join('_')}.xlsx`;
  }

  private slugify(value: string): string {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '') || 'cuenta';
  }

  private areQueriesEqual(left: ReportsQuery, right: ReportsQuery): boolean {
    return (left.from ?? '') === (right.from ?? '')
      && (left.to ?? '') === (right.to ?? '')
      && (left.accountId ?? '') === (right.accountId ?? '')
      && this.areChannelSelectionsEqual(left.channels, right.channels);
  }

  private areChannelSelectionsEqual(left: string[] | undefined, right: string[] | undefined): boolean {
    const leftChannels = [...new Set(left ?? [])].sort();
    const rightChannels = [...new Set(right ?? [])].sort();

    return leftChannels.length === rightChannels.length
      && leftChannels.every((channel, index) => channel === rightChannels[index]);
  }

  private updateAccountOptions(accounts: ReportAccount[]): void {
    this.accountOptions = [
      { label: 'Todas las cuentas', value: null },
      ...accounts.map(account => ({
        label: account.name,
        value: account.accountId
      }))
    ];

    const selectedStillExists = this.accountOptions.some(option => option.value === this.selectedAccountId);
    if (!selectedStillExists) {
      this.selectedAccountId = null;
    }
  }

  private updateChannelOptions(reports: MonthlyConversationReport[]): void {
    const knownChannels = new Map(this.channelOptions.map(option => [option.value, option]));

    for (const report of reports) {
      for (const channel of report.channels) {
        if (channel.channel && !knownChannels.has(channel.channel)) {
          knownChannels.set(channel.channel, {
            label: this.formatChannelName(channel.channel),
            value: channel.channel,
            icon: this.getChannelIcon(channel.channel)
          });
        }
      }
    }

    this.channelOptions = [...knownChannels.values()]
      .sort((left, right) => left.label.localeCompare(right.label, 'es'));
  }

  private getChannelIcon(channel: string): string {
    const icons: Record<string, string> = {
      LIVE_CHAT: 'pi pi-comments',
      WHATSAPP: 'pi pi-phone',
      EMAIL: 'pi pi-envelope',
      FACEBOOK_MESSENGER: 'pi pi-comments',
      INSTAGRAM_MESSAGING: 'pi pi-image',
      FACEBOOK_POST: 'pi pi-megaphone',
      INSTAGRAM_POST: 'pi pi-megaphone',
      chatwoot: 'pi pi-comments'
    };

    return icons[channel] ?? 'pi pi-comment';
  }

  private getSelectedChannelsLabel(): string {
    if (this.selectedChannels.length === 0) {
      return 'Todos los canales';
    }

    return this.selectedChannels
      .map(channel => this.channelOptions.find(option => option.value === channel)?.label ?? this.formatChannelName(channel))
      .join(', ');
  }

  private emptyTotals(): ReportTotals {
    return {
      conversations: 0,
      messages: 0,
      inbound: 0,
      outbound: 0,
      activeChannels: 0,
      averageMessagesPerConversation: 0,
      inboundPercentage: 0,
      outboundPercentage: 0
    };
  }

  private setError(message: string): void {
    this.errorMessage = message;
    this.messageService.add({ severity: 'error', summary: 'Error', detail: message });
    this.cdr.markForCheck();
  }

  private getFriendlyErrorMessage(error: unknown): string {
    const message = error instanceof Error ? error.message : '';

    if (message.includes('from must be less than or equal to to')) {
      return 'La fecha desde debe ser menor o igual a la fecha hasta.';
    }

    if (message.includes('accountId is not configured for reports')) {
      return 'La cuenta seleccionada no está configurada para reportes.';
    }

    return 'No se pudieron cargar los reportes. Intenta nuevamente.';
  }
}
